import { dropdownName } from "../../utils/common";

/**
 * All employee_ids in a manager's reporting tree (direct + nested subordinates).
 * Walks manager_id links recursively; guards against cycles.
 */
export function collectDescendantEmployeeIds(
  activeEmployees = [],
  rootManagerId,
  { includeSelf = false } = {},
) {
  if (!rootManagerId || !Array.isArray(activeEmployees) || !activeEmployees.length) {
    return new Set();
  }

  const childrenByManager = new Map();
  for (const emp of activeEmployees) {
    if (emp.manager_id == null || emp.manager_id === "") continue;
    const managerKey = String(emp.manager_id);
    if (!childrenByManager.has(managerKey)) {
      childrenByManager.set(managerKey, []);
    }
    childrenByManager.get(managerKey).push(emp);
  }

  const descendantIds = new Set();
  const visitedManagers = new Set();

  const walk = (managerId) => {
    const key = String(managerId);
    if (visitedManagers.has(key)) return;
    visitedManagers.add(key);

    const reports = childrenByManager.get(key) || [];
    for (const emp of reports) {
      const employeeId = String(emp.employee_id);
      if (descendantIds.has(employeeId)) continue;
      descendantIds.add(employeeId);
      walk(employeeId);
    }
  };

  walk(rootManagerId);

  if (includeSelf) {
    descendantIds.add(String(rootManagerId));
  }

  return descendantIds;
}

/** Full reporting tree under a manager + the manager (for self-assigned clients). */
export function buildTeamScope(activeEmployees = [], managerId) {
  if (!managerId || !Array.isArray(activeEmployees) || !activeEmployees.length) {
    return { teamIds: null, teamNames: null };
  }

  const descendantIds = collectDescendantEmployeeIds(activeEmployees, managerId, {
    includeSelf: false,
  });
  const managerSelf = activeEmployees.find(
    (emp) => String(emp.employee_id) === String(managerId),
  );
  const roster = [
    ...(managerSelf ? [managerSelf] : []),
    ...activeEmployees.filter((emp) =>
      descendantIds.has(String(emp.employee_id)),
    ),
  ];

  const teamIds = new Set(roster.map((emp) => String(emp.employee_id)));
  const teamNames = new Set(
    roster
      .map((emp) => dropdownName(emp).trim().toLowerCase())
      .filter(Boolean),
  );

  return { teamIds, teamNames };
}

function parseAssignedIds(assigned) {
  if (Array.isArray(assigned)) return assigned;
  if (typeof assigned === "string" && assigned.trim()) {
    try {
      const parsed = JSON.parse(assigned);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return assigned.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
}

export function recordBelongsToTeam(record, scope) {
  if (!scope?.teamIds) return true;
  if (!record) return false;

  const { teamIds, teamNames } = scope;

  const idFields = [
    record.employee_id,
    record.sales,
    record.billed_by,
    record.created_by,
    record.handled_by,
  ];
  if (idFields.some((id) => id != null && teamIds.has(String(id)))) return true;

  const assigned = parseAssignedIds(record.assigned_employee_ids);
  if (assigned.some((id) => teamIds.has(String(id)))) return true;

  const displayName = (record.employee || record.employee_name || "")
    .trim()
    .toLowerCase();
  if (displayName && teamNames.has(displayName)) return true;

  const billFrom = `${record.bill_from_first_name || ""} ${record.bill_from_last_name || ""}`
    .trim()
    .toLowerCase();
  if (billFrom && teamNames.has(billFrom)) return true;

  return false;
}

export function filterForTeam(list, scope) {
  if (!scope?.teamIds) return list || [];
  return (list || []).filter((row) => recordBelongsToTeam(row, scope));
}

export function appendManagerReportFilters(
  filters = {},
  isManagerView,
  managerId,
  { revenueByEmployee = false } = {},
) {
  const next = { ...filters };
  if (revenueByEmployee) next.revenue_by_employee = true;
  if (!isManagerView || !managerId) return next;
  next.manager_id = managerId;
  next.include_subordinate_tree = true;
  return next;
}
